import { type WorkflowCategory } from '@apm/contracts';

/**
 * Màu của một cột trạng thái, đủ để **tô kín một ô bảng** — APM-LIST-a.
 *
 * ## Vì sao trả hex chứ không trả lớp Tailwind
 *
 * Màu này đến từ CSDL (`workflow_status.color`, người dùng tự đặt). Tailwind
 * quét **mã nguồn** để biết sinh lớp nào, nên một lớp ghép từ giá trị lúc chạy
 * không tồn tại: ô hiện đúng chỗ, đúng kích thước, và **không màu** — cạm bẫy
 * đã ghi ở CLAUDE.md và đã cắn ở APM-1303. Giá trị ở đây đi vào `style`.
 *
 * ## Vì sao phải tính luôn màu chữ
 *
 * Một badge nhỏ chữ đen trên nền nhạt thì đọc được bằng bất cứ màu nào. Một ô
 * **tô kín** thì không: nền vàng nhạt với chữ trắng là một ô không đọc nổi, và
 * người đặt màu không có cách nào biết trước điều đó. Nên chữ do đây quyết,
 * không phải do người đặt màu.
 */

/**
 * Màu dự phòng khi cột **chưa được đặt màu**.
 *
 * Xếp theo nhóm trạng thái chứ không băm ngẫu nhiên cả bảng: "chưa làm" phải
 * đọc ra là nguội, "đang làm" là ấm, "xong" là xanh — kể cả với người chưa bao
 * giờ mở phần cấu hình cột. Ba sắc trong mỗi nhóm để hai cột cùng nhóm (vd
 * "Chờ duyệt" và "Cần làm") không trùng khít nhau.
 */
const FALLBACK: Record<WorkflowCategory, readonly string[]> = {
  todo: ['#c4c8d4', '#a9b0c2', '#8f97ad'],
  in_progress: ['#fdab3d', '#f0a04b', '#e8873a'],
  done: ['#33d391', '#2bb673', '#22a45d'],
};

/**
 * Bảng màu **chọn được** cho một cột — APM-BOARD-j.
 *
 * ## Vì sao một bảng đóng, không phải ô chọn màu tự do
 *
 * Ô trạng thái ở bảng Danh sách **tô kín cả ô**, nên màu phải đủ đậm để chữ
 * trắng hoặc chữ đen đọc được trên đó. Một `<input type="color">` cho phép
 * chọn vàng chanh và xám nhạt — hai màu mà không màu chữ nào cứu được, và
 * người chọn không có cách nào biết trước điều đó.
 *
 * Mười hai màu này đều đã qua `readableOn`, và chúng gồm cả ba họ mà
 * `FALLBACK` dùng — nên một cột tự đặt màu và một cột để trống vẫn nằm trong
 * cùng một bảng màu, thay vì trông như hai hệ thống khác nhau.
 */
export const STATUS_PALETTE = [
  '#c4c8d4',
  '#8f97ad',
  '#579bfc',
  '#0073ea',
  '#a25ddc',
  '#e879f9',
  '#ff7575',
  '#e2445c',
  '#fdab3d',
  '#f0a04b',
  '#33d391',
  '#00854d',
] as const;

/** FNV-1a — cùng thuật toán với `projectTone` và `sprintColor`. */
function hashOf(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(hash ^ value.charCodeAt(index), 16_777_619);
  }
  return hash >>> 0;
}

/** `#rgb` hoặc `#rrggbb` → ba kênh 0–255. `null` nếu không đọc được. */
function channels(hex: string): [number, number, number] | null {
  const raw = hex.trim().replace('#', '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((ch) => ch + ch)
          .join('')
      : raw;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

/**
 * Chữ trắng hay chữ đen trên nền này.
 *
 * Độ sáng cảm nhận theo WCAG (hệ số 0.2126/0.7152/0.0722) chứ không phải trung
 * bình ba kênh: mắt người nhạy với xanh lá gấp nhiều lần với xanh lam, nên một
 * phép trung bình sẽ kết luận sai với đúng những màu hay dùng nhất — cam và
 * xanh lá.
 *
 * Ngưỡng 0.6 chứ không 0.5: ở quanh giữa thì chữ trắng đã bắt đầu khó đọc
 * trong khi chữ đen vẫn còn rõ, nên nghiêng về phía an toàn.
 */
export function readableOn(background: string): string {
  const rgb = channels(background);
  if (!rgb) return '#ffffff';
  const [r, g, b] = rgb.map((channel) => channel / 255) as [number, number, number];
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.6 ? '#323338' : '#ffffff';
}

export interface StatusTone {
  /** Nền của ô, dùng cho `style.backgroundColor`. */
  readonly background: string;
  /** Màu chữ đọc được trên nền ấy. */
  readonly foreground: string;
}

/**
 * Màu của một cột. `color` để trống thì suy ra từ nhóm + băm `id` — ổn định
 * giữa các lần tải, nên cùng một cột luôn ra cùng một màu ở mọi màn hình.
 */
export function statusTone(status: {
  readonly id: string;
  readonly category: WorkflowCategory;
  readonly color: string | null;
}): StatusTone {
  const family = FALLBACK[status.category];
  const background =
    status.color && channels(status.color)
      ? status.color
      : family[hashOf(status.id) % family.length]!;

  return { background, foreground: readableOn(background) };
}

/**
 * Màu của bốn mức ưu tiên — APM-LIST-a.
 *
 * Cố định, **không** cho đặt: ưu tiên là một thang có thứ tự, và một thang mà
 * mỗi dự án tô một kiểu thì không so được giữa hai dự án. Đi từ xám (thấp) lên
 * đỏ (gấp) để mắt đọc ra thứ bậc mà không cần đọc chữ.
 */
const PRIORITY: Record<string, string> = {
  low: '#9aa3b8',
  medium: '#579bfc',
  high: '#5559df',
  urgent: '#e2445c',
};

export function priorityToneOf(priority: string): StatusTone {
  const background = PRIORITY[priority] ?? PRIORITY.low!;
  return { background, foreground: readableOn(background) };
}
